import { Link } from '@solidjs/router';
import { Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { LockedContent } from '../../../ui-components/layouts/locked-content';
import { SLIDE_STRETCH, SliderCardMedia } from '../slider-card-media';
import { SliderCardData } from './slider-card-types';

type Props = {
    data: SliderCardData;
};

/* Same flat surface as the event/editorial card: no border, no hover shadow — the rounded
   media band (SliderCardMedia) is the only framed element and the text sits flush below. */
const CARD_SURFACE = 'relative grid h-full grid-rows-[auto_auto_1fr] gap-cvi-4';

/* The one card every post type in an `atos/slider` renders as. The rows are a fixed
   grid — media, a two-line title band, then the body — so a slider
   holding a product next to a video next to a blog post still lines up. */
export const SliderCard = (props: Props) => {
    const { userState, pageRegistration } = useContext(AppContext);

    const isHiddenForPublic = () => Boolean(
        props.data.isHealthcareProfessional
            && !props.data.isVisibleForPublic
            && !userState.user
            && pageRegistration.url !== '',
    );

    const isInteractive = () => Boolean(props.data.href || props.data.onClick);

    /* The hit area is a sibling of the card, not its parent: the favourite heart is a
       button and must not be nested inside a link. See the stacking note on
       video-post-card.tsx — the overlay sits at z-10, the heart at z-20. */
    const hitArea = () => {
        const classes = clsx(
            'absolute inset-0 z-10 rounded-cvi-md no-underline',
            isHiddenForPublic() && 'pointer-events-none',
        );

        if (props.data.onClick) {
            return (
                <button
                    type="button"
                    class={clsx(classes, 'border-none bg-transparent p-0')}
                    onClick={() => props.data.onClick?.()}
                >
                    <span class="sr-only">{props.data.heading}</span>
                </button>
            );
        }

        return (
            <Link href={props.data.href || ''} target={props.data.target} class={classes}>
                <span class="sr-only">{props.data.heading}</span>
            </Link>
        );
    };

    return (
        <div class={clsx(SLIDE_STRETCH, 'group relative block w-full')}>
            <div class={CARD_SURFACE}>
                <SliderCardMedia
                    image={props.data.image}
                    fit={props.data.mediaFit}
                    videoUrl={props.data.videoUrl}
                    showPlayOverlay={props.data.showPlayOverlay}
                    categoryLabel={props.data.categoryLabel}
                    myContent={props.data.myContent}
                />

                {/* Reserves two lines whatever the title's length, so the excerpt
                    below it starts on the same line in every card of the row. */}
                <h3 class="card-title-clamp mt-cvi-4 heading-lg desktop:text-heading-md desktop:leading-heading-md font-cvi-medium text-cvi-fg-default m-0">
                    {props.data.heading}
                </h3>

                <div class="flex flex-col gap-cvi-4">
                    <Show when={props.data.text}>
                        <p class="body-sm font-cvi-regular text-cvi-fg-muted m-0 line-clamp-3">
                            {props.data.text}
                        </p>
                    </Show>

                    {/* A span, not a Button — the card is already the link. */}
                    <Show when={props.data.href && props.data.linkTitle}>
                        <span class="mt-auto label-md font-cvi-bold underline text-cvi-fg-link transition-colors duration-200 group-hover:text-[var(--component-button-color-tertiary-hover-text-color)]">
                            {props.data.linkTitle}
                        </span>
                    </Show>
                </div>

                <Show when={isHiddenForPublic()}>
                    <LockedContent isColumn={true} />
                </Show>
            </div>

            <Show when={isInteractive()}>
                {hitArea()}
            </Show>
        </div>
    );
};
